﻿/**
 * Many of the Babylon mesh builder methods take a faceUV array as a part of the options object.
 * Each item in the faceUV array is a BABYLON.Vector4 object describing which part (sprite) of
 * the material's texture should be mapped to each face of the resulting 3D mesh object. This
 * FaceUVHelper class has helper functions for setting up the faceUV array as well as orienting
 * the faceUV items such that they are displayed correctly.
 *
 * This class was developed as a part of the prototyping of our mesh basket here:
 * https://playground.babylonjs.com/#WVLDLM#35
 */
export class FaceUVHelper {
  /**
   * This function in-place updates a faceUV item to flip the item sprite (texture area) horizontally over the Y-axis.
   *
   * See https://doc.babylonjs.com/features/featuresDeepDive/materials/using/texturePerBoxFace#how-to-orientate-a-sprite-on-a-face-with-faceuv.
   * @param {array} faceUV Each item in the faceUV array is a BABYLON.Vector4 object whose constructor
   * has parameters x, y, z, w.
   * @param {int} index Index into the item in the faceUV array we are updating.
   */
  flipUVOverYAxis(faceUV, index) {
    if (faceUV.length >= index + 1) {
      let xLeft = faceUV[index].x;
      //let yBottom = faceUV[index].y;
      let xRight = faceUV[index].z;
      //let yTop = faceUV[index].w;
      faceUV[index].x = xRight;
      faceUV[index].z = xLeft;
    }
  }

  /**
   * This function in-place updates a faceUV item to flip the item sprite (texture area) vertically over the X-axis.
   *
   * See https://doc.babylonjs.com/features/featuresDeepDive/materials/using/texturePerBoxFace#how-to-orientate-a-sprite-on-a-face-with-faceuv.
   * @param {array} faceUV Each item in the faceUV array is a BABYLON.Vector4 object whose constructor
   * has parameters x, y, z, w.
   * @param {int} index Index into the item in the faceUV array we are updating.
   */
  flipUVOverXAxis(faceUV, index) {
    if (faceUV.length >= index + 1) {
      //let xLeft = faceUV[index].x;
      let yBottom = faceUV[index].y;
      //let xRight = faceUV[index].z;
      let yTop = faceUV[index].w;
      faceUV[index].y = yTop;
      faceUV[index].w = yBottom;
    }
  }

  /**
   * Creates a faceUV array for an image with sprites divided into columns and rows.
   * @param {int} columns Column count
   * @param {int} rows Row count
   * @param {bool} useFullImageOnEachSprite If true, then each faceUV item will be initialized with (0, 0, 1, 1)
   * @returns An array of BABYLON.Vector4
   */
  setupFaceUV(columns, rows, useFullImageOnEachSprite) {
    let faceUV = new Array(rows * columns);
    if (useFullImageOnEachSprite) {
      for (let x = 0; x < columns; x++) {
        for (let y = 0; y < rows; y++) {
          faceUV[x] = new BABYLON.Vector4(0, 0, 1, 1);
        }
      }
    } else {
      for (let x = 0; x < columns; x++) {
        const xLeft = x / columns;
        const xRight = (x + 1) / columns;
        for (let y = 0; y < rows; y++) {
          const yBottom = y / rows;
          const yTop = (y + 1) / rows;
          faceUV[x] = new BABYLON.Vector4(xLeft, yBottom, xRight, yTop);
        }
      }
    }
    return faceUV;
  }
}
